// The kit's Tooltip card: a dark tooltip above the element with a small arrow, no wider than the kit allows
// (the kit sets the width), shown on hover and on keyboard focus. The trigger is wrapped in `tip`;
// the floating layer `ftip` is drawn on the body so a table row or panel never clips it.
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type TipProps = {
  text: string;
  children: ReactNode;
  /** Focusable when the child is not (a pill, a tag), so keyboard users get the text too. */
  focusable?: boolean;
  /** Layout only, for a trigger that fills its row (a menu item). */
  style?: CSSProperties;
};

export function Tip({ text, children, focusable = false, style }: TipProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  // How far the layer moves sideways to stay inside the window; the arrow stays on the trigger.
  const [shift, setShift] = useState(0);

  const show = () => {
    const box = ref.current?.getBoundingClientRect();
    if (box) setAt({ x: box.left + box.width / 2, y: box.top });
  };
  const hide = () => {
    setAt(null);
    setShift(0);
  };

  useLayoutEffect(() => {
    const tip = tipRef.current;
    if (!at || !tip) return;
    const margin = 8;
    const half = tip.offsetWidth / 2;
    const right = window.innerWidth - margin - (at.x + half);
    const left = at.x - half - margin;
    setShift(right < 0 ? right : left < 0 ? -left : 0);
  }, [at, text]);

  return (
    <span
      ref={ref}
      className="tip"
      style={style}
      tabIndex={focusable ? 0 : undefined}
      aria-label={focusable ? text : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {at &&
        createPortal(
          <div
            ref={tipRef}
            className="ftip on"
            role="tooltip"
            // Centered above the trigger (moved in when it would leave the window): the layer moves up
            // by its own height plus the arrow.
            style={{
              left: at.x + shift,
              top: at.y,
              transform: 'translate(-50%, calc(-100% - 0.5rem))',
            }}
          >
            {text}
            <span className="ftip-a" style={{ left: `calc(50% - ${shift}px)` }} />
          </div>,
          document.body,
        )}
    </span>
  );
}
